/**
 * Environment configuration
 * 
 * This file handles environment-specific configuration and provides
 * a centralized way to access environment variables.
 */

// Environment types
export type Environment = 'development' | 'test' | 'production';

// Get current environment
export const getEnvironment = (): Environment => {
  const env = process.env.REACT_APP_ENV || process.env.NODE_ENV;
  if (env === 'production' || env === 'development' || env === 'test') {
    return env;
  }
  return 'development'; // Default to development
};

export type ELogLevel = 'debug' | 'info' | 'warn' | 'error';

// Environment-specific configuration
interface EnvironmentConfig {
  apiUrl: string;
  logLevel: ELogLevel;
  enableAnalytics: boolean;
  enablePerformanceMonitoring: boolean;
}

// Configuration by environment
const environmentConfigs: Record<Environment, EnvironmentConfig> = {
  development: {
    apiUrl: process.env.REACT_APP_API_URL || 'http://localhost:5000',
    logLevel: 'debug',
    enableAnalytics: false,
    enablePerformanceMonitoring: false,
  },
  test: {
    apiUrl: process.env.REACT_APP_API_URL || 'http://localhost:5000',
    logLevel: 'info',
    enableAnalytics: false,
    enablePerformanceMonitoring: false,
  },
  production: {
    apiUrl: process.env.REACT_APP_API_URL || 'https://api.ustaxassistant.com',
    logLevel: 'error',
    enableAnalytics: true,
    enablePerformanceMonitoring: true,
  },
};

// Get current environment configuration
export const getConfig = (): EnvironmentConfig => {
  const env = getEnvironment();
  return environmentConfigs[env];
};

// Firebase configuration
export interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId?: string;
}

// Get Firebase configuration
export const getFirebaseConfig = (): FirebaseConfig => {
  return {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY || '',
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || '',
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || '',
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || '',
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '',
    appId: process.env.REACT_APP_FIREBASE_APP_ID || '',
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
  };
};

// AI API configuration
export interface AIConfig {
  apiKey: string;
  endpoint?: string;
}

// Get AI API configuration
export const getAIConfig = (): AIConfig => {
  return {
    apiKey: process.env.REACT_APP_GEMINI_API_KEY || '',
    endpoint: process.env.REACT_APP_GEMINI_API_ENDPOINT,
  };
};

// Validate required environment variables
export const validateEnvironment = (): string[] => {
  const errors: string[] = [];
  const requiredVars = [
    'REACT_APP_FIREBASE_API_KEY',
    'REACT_APP_FIREBASE_AUTH_DOMAIN',
    'REACT_APP_FIREBASE_PROJECT_ID',
    'REACT_APP_FIREBASE_STORAGE_BUCKET',
    'REACT_APP_FIREBASE_MESSAGING_SENDER_ID',
    'REACT_APP_FIREBASE_APP_ID',
  ];

  requiredVars.forEach(varName => {
    if (!process.env[varName]) {
      errors.push(`Missing required environment variable: ${varName}`);
    }
  });

  return errors;
}; 